// servicios/actualizacionPresupuestos.js
import { supabase } from "../supabase";

// =================================================================
// FUNCIÓN PRINCIPAL DE SINCRONIZACIÓN DE PRESUPUESTOS
// =================================================================

export const sincronizarPresupuestosProyecto = async (
  proyectoId,
  constructoraId
) => {
  try {
    console.log(`🔄 Sincronizando presupuestos del proyecto ${proyectoId}`);

    // 1. Obtener datos del proyecto
    const { data: proyecto, error: errorProyecto } = await supabase
      .from("proyectos")
      .select("*")
      .eq("id", proyectoId)
      .eq("constructora_id", constructoraId)
      .single();

    if (errorProyecto || !proyecto) {
      throw new Error("Proyecto no encontrado");
    }

    // 2. Obtener cotización del dólar
    let cotizacionDolar = 1000;
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      cotizacionDolar = data.venta;
    } catch (err) {
      console.warn("Usando cotización por defecto:", cotizacionDolar);
    }

    // 3. Calcular todos los presupuestos
    const presupuestosParaGuardar = [];

    // 3A. Procesar presupuestos de obra
    const presupuestosObra = await procesarPresupuestosObra(
      proyecto,
      cotizacionDolar
    );
    presupuestosParaGuardar.push(...presupuestosObra);

    // 3B. Procesar honorarios
    if (proyecto.honorarios) {
      const honorarios = await procesarHonorarios(proyecto, cotizacionDolar);
      if (honorarios) presupuestosParaGuardar.push(honorarios);
    }

    // 3C. Procesar mano de obra
    if (proyecto.mano_obra_separada && proyecto.monto_mano_obra) {
      const manoObra = await procesarManoObra(proyecto, cotizacionDolar);
      if (manoObra) presupuestosParaGuardar.push(manoObra);
    }

    // 3D. Procesar llave en mano
    if (
      proyecto.tipo_obra === "llave_en_mano" &&
      proyecto.monto_total_llave_en_mano
    ) {
      const llaveEnMano = await procesarLlaveEnMano(proyecto, cotizacionDolar);
      if (llaveEnMano) presupuestosParaGuardar.push(llaveEnMano);
    }

    // 4. UPSERT real con clave única estable
    if (presupuestosParaGuardar.length > 0) {
      console.log(
        `🔄 Actualizando ${presupuestosParaGuardar.length} presupuestos con IDs estables...`
      );

      const { error } = await supabase
        .from("presupuestos_actualizados")
        .upsert(presupuestosParaGuardar, {
          onConflict: "clave_unica",
        });

      if (error) {
        console.error("❌ Error en upsert:", error);
        throw new Error(`Error en upsert: ${error.message}`);
      }

      console.log(
        `✅ ${presupuestosParaGuardar.length} presupuestos sincronizados con IDs estables`
      );
    }
    console.log(
      `✅ Proyecto ${proyectoId} sincronizado con ${presupuestosParaGuardar.length} presupuestos`
    );
    return { success: true, cantidad: presupuestosParaGuardar.length };
  } catch (error) {
    console.error(`❌ Error sincronizando proyecto ${proyectoId}:`, error);
    return { success: false, error: error.message };
  }
};

// =================================================================
// FUNCIONES AUXILIARES
// =================================================================

const procesarPresupuestosObra = async (proyecto, cotizacionDolar) => {
  try {
    // Obtener presupuestos iniciales
    const { data: presupuestos, error } = await supabase
      .from("presupuestos_iniciales")
      .select(
        `
        *,
        etapas_obra!inner(nombre),
        subetapas_obra!inner(nombre),
        conceptos_salida!inner(nombre)
      `
      )
      .eq("proyecto_id", proyecto.id);

    if (error || !presupuestos) return [];

    const presupuestosConDatos = [];

    for (const presupuesto of presupuestos) {
      // Calcular gastos asociados (solo salidas activas y no eliminadas)
      const { data: gastos } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, fecha")
        .eq("presupuesto_inicial_id", presupuesto.id)
        .eq("activo", true)
        .is("deleted_at", null);

      const totalGastado = (gastos || []).reduce((sum, gasto) => {
        return (
          sum +
          convertirMonto(
            gasto.monto,
            gasto.moneda,
            presupuesto.moneda,
            gasto.tipo_cambio
          )
        );
      }, 0);

      // Aplicar CAC si es necesario
      const presupuestoConCAC = await aplicarCAC(
        presupuesto,
        gastos || [],
        cotizacionDolar
      );

      // Convertir a ambas monedas
      const montoActualizado =
        presupuestoConCAC.monto_actualizado || presupuesto.monto_presupuestado;
      const saldoPendiente =
        presupuestoConCAC.saldo_actualizado ||
        presupuesto.monto_presupuestado - totalGastado;

      presupuestosConDatos.push({
        constructora_id: proyecto.constructora_id,
        proyecto_id: proyecto.id,
        etapa_id: presupuesto.etapa_id,
        subetapa_id: presupuesto.subetapa_id,
        presupuesto_inicial_id: presupuesto.id,
        clave_unica: `${proyecto.id}_obra_${presupuesto.id}`, // ← AGREGAR ESTA LÍNEA

        nombre_presupuesto: presupuesto.nombre,
        descripcion: presupuesto.descripcion,
        tipo: "obra",
        total_actualizado_ars: convertirMonto(
          montoActualizado,
          presupuesto.moneda,
          "ARS",
          cotizacionDolar
        ),
        total_pagado_ars: convertirMonto(
          totalGastado,
          presupuesto.moneda,
          "ARS",
          cotizacionDolar
        ),
        saldo_ars: convertirMonto(
          saldoPendiente,
          presupuesto.moneda,
          "ARS",
          cotizacionDolar
        ),
        total_actualizado_usd: convertirMonto(
          montoActualizado,
          presupuesto.moneda,
          "USD",
          cotizacionDolar
        ),
        total_pagado_usd: convertirMonto(
          totalGastado,
          presupuesto.moneda,
          "USD",
          cotizacionDolar
        ),
        saldo_usd: convertirMonto(
          saldoPendiente,
          presupuesto.moneda,
          "USD",
          cotizacionDolar
        ),
        porcentaje_ejecutado:
          montoActualizado > 0 ? (totalGastado / montoActualizado) * 100 : 0,
        moneda_original: presupuesto.moneda,
        estado: presupuesto.estado,
      });
    }

    return presupuestosConDatos;
  } catch (error) {
    console.error("Error procesando presupuestos de obra:", error);
    return [];
  }
};

const procesarHonorarios = async (proyecto, cotizacionDolar) => {
  try {
    // Obtener pagos de honorarios (misma lógica que en tu código)
    const { data: pagos } = await supabase
      .from("entradas")
      .select(
        `
    fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante,
    conceptos_entrada!inner(nombre)
  `
      )
      .eq("proyecto_id", proyecto.id)
      .ilike("conceptos_entrada.nombre", "%honorario%");
    const totalPagado = (pagos || []).reduce((sum, pago) => {
      return (
        sum +
        convertirMonto(
          pago.monto,
          pago.moneda,
          proyecto.moneda_honorarios,
          pago.tipo_cambio
        )
      );
    }, 0);

    // Aplicar sistema de actualización si corresponde
    let montoActualizado = proyecto.honorarios;
    if (proyecto.honorarios_se_actualizan) {
      // Aquí iría la lógica de actualización por CAC/sistema personalizado
      // Por ahora usamos el monto original
    }

    const saldoPendiente = montoActualizado - totalPagado;

    return {
      constructora_id: proyecto.constructora_id,
      proyecto_id: proyecto.id,
      etapa_id: null,
      subetapa_id: null,
      presupuesto_inicial_id: null,
      clave_unica: `${proyecto.id}_honorarios`, // ← AGREGAR ESTA LÍNEA

      nombre_presupuesto: "Honorarios Profesionales",
      descripcion: null,
      tipo: "honorarios",
      total_actualizado_ars: convertirMonto(
        montoActualizado,
        proyecto.moneda_honorarios,
        "ARS",
        cotizacionDolar
      ),
      total_pagado_ars: convertirMonto(
        totalPagado,
        proyecto.moneda_honorarios,
        "ARS",
        cotizacionDolar
      ),
      saldo_ars: convertirMonto(
        saldoPendiente,
        proyecto.moneda_honorarios,
        "ARS",
        cotizacionDolar
      ),
      total_actualizado_usd: convertirMonto(
        montoActualizado,
        proyecto.moneda_honorarios,
        "USD",
        cotizacionDolar
      ),
      total_pagado_usd: convertirMonto(
        totalPagado,
        proyecto.moneda_honorarios,
        "USD",
        cotizacionDolar
      ),
      saldo_usd: convertirMonto(
        saldoPendiente,
        proyecto.moneda_honorarios,
        "USD",
        cotizacionDolar
      ),
      porcentaje_ejecutado:
        montoActualizado > 0 ? (totalPagado / montoActualizado) * 100 : 0,
      moneda_original: proyecto.moneda_honorarios,
      estado: "abierto",
    };
  } catch (error) {
    console.error("Error procesando honorarios:", error);
    return null;
  }
};

const procesarManoObra = async (proyecto, cotizacionDolar) => {
  try {
    // Obtener cajas de mano de obra y pagos
    const { data: cajas } = await supabase
      .from("cajas")
      .select("id")
      .eq("proyecto_id", proyecto.id)
      .eq("tipo_caja", "mano_obra");

    const idsCajas = cajas?.map((c) => c.id) || [];
    let totalPagado = 0;

    if (idsCajas.length > 0) {
      const { data: pagos } = await supabase
        .from("entradas")
        .select("fecha, monto, moneda, tipo_cambio")
        .eq("proyecto_id", proyecto.id)
        .in("caja_destino_id", idsCajas);

      totalPagado = (pagos || []).reduce((sum, pago) => {
        return (
          sum +
          convertirMonto(
            pago.monto,
            pago.moneda,
            proyecto.moneda_mano_obra,
            pago.tipo_cambio
          )
        );
      }, 0);
    }

    let montoActualizado = proyecto.monto_mano_obra;
    if (proyecto.mano_obra_se_actualiza) {
      // Aquí iría la lógica de actualización
    }

    const saldoPendiente = montoActualizado - totalPagado;

    return {
      constructora_id: proyecto.constructora_id,
      proyecto_id: proyecto.id,
      etapa_id: null,
      subetapa_id: null,
      presupuesto_inicial_id: null,
      clave_unica: `${proyecto.id}_mano_obra`, // ← AGREGAR ESTA LÍNEA
      nombre_presupuesto: "Mano de Obra",
      descripcion: null,
      tipo: "mano_obra",
      total_actualizado_ars: convertirMonto(
        montoActualizado,
        proyecto.moneda_mano_obra,
        "ARS",
        cotizacionDolar
      ),
      total_pagado_ars: convertirMonto(
        totalPagado,
        proyecto.moneda_mano_obra,
        "ARS",
        cotizacionDolar
      ),
      saldo_ars: convertirMonto(
        saldoPendiente,
        proyecto.moneda_mano_obra,
        "ARS",
        cotizacionDolar
      ),
      total_actualizado_usd: convertirMonto(
        montoActualizado,
        proyecto.moneda_mano_obra,
        "USD",
        cotizacionDolar
      ),
      total_pagado_usd: convertirMonto(
        totalPagado,
        proyecto.moneda_mano_obra,
        "USD",
        cotizacionDolar
      ),
      saldo_usd: convertirMonto(
        saldoPendiente,
        proyecto.moneda_mano_obra,
        "USD",
        cotizacionDolar
      ),
      porcentaje_ejecutado:
        montoActualizado > 0 ? (totalPagado / montoActualizado) * 100 : 0,
      moneda_original: proyecto.moneda_mano_obra,
      estado: "abierto",
    };
  } catch (error) {
    console.error("Error procesando mano de obra:", error);
    return null;
  }
};

const procesarLlaveEnMano = async (proyecto, cotizacionDolar) => {
  try {
    // Obtener todos los pagos del proyecto
    const { data: pagos } = await supabase
      .from("entradas")
      .select("fecha, monto, moneda, tipo_cambio")
      .eq("proyecto_id", proyecto.id);

    const totalPagado = (pagos || []).reduce((sum, pago) => {
      return (
        sum +
        convertirMonto(
          pago.monto,
          pago.moneda,
          proyecto.moneda_llave_en_mano,
          pago.tipo_cambio
        )
      );
    }, 0);

    let montoActualizado = proyecto.monto_total_llave_en_mano;
    if (proyecto.metodo_actualizacion) {
      // Aquí iría la lógica de actualización
    }

    const saldoPendiente = montoActualizado - totalPagado;

    return {
      constructora_id: proyecto.constructora_id,
      proyecto_id: proyecto.id,
      etapa_id: null,
      subetapa_id: null,
      presupuesto_inicial_id: null,
      clave_unica: `${proyecto.id}_llave_en_mano`, // ← AGREGAR ESTA LÍNEA
      nombre_presupuesto: "Costo Total de la Obra",
      descripcion: null,
      tipo: "llave_en_mano",
      total_actualizado_ars: convertirMonto(
        montoActualizado,
        proyecto.moneda_llave_en_mano,
        "ARS",
        cotizacionDolar
      ),
      total_pagado_ars: convertirMonto(
        totalPagado,
        proyecto.moneda_llave_en_mano,
        "ARS",
        cotizacionDolar
      ),
      saldo_ars: convertirMonto(
        saldoPendiente,
        proyecto.moneda_llave_en_mano,
        "ARS",
        cotizacionDolar
      ),
      total_actualizado_usd: convertirMonto(
        montoActualizado,
        proyecto.moneda_llave_en_mano,
        "USD",
        cotizacionDolar
      ),
      total_pagado_usd: convertirMonto(
        totalPagado,
        proyecto.moneda_llave_en_mano,
        "USD",
        cotizacionDolar
      ),
      saldo_usd: convertirMonto(
        saldoPendiente,
        proyecto.moneda_llave_en_mano,
        "USD",
        cotizacionDolar
      ),
      porcentaje_ejecutado:
        montoActualizado > 0 ? (totalPagado / montoActualizado) * 100 : 0,
      moneda_original: proyecto.moneda_llave_en_mano,
      estado: "abierto",
    };
  } catch (error) {
    console.error("Error procesando llave en mano:", error);
    return null;
  }
};

// Funciones auxiliares
const convertirMonto = (
  monto,
  monedaOrigen,
  monedaDestino,
  tipoCambio = null
) => {
  const montoNum = parseFloat(monto || 0);
  if (monedaOrigen === monedaDestino) return montoNum;

  const tc = tipoCambio || 1000;
  if (monedaOrigen === "USD" && monedaDestino === "ARS") {
    return montoNum * tc;
  } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
    return montoNum / tc;
  }
  return montoNum;
};

const aplicarCAC = async (presupuesto, gastos, cotizacionDolar) => {
  // Por ahora retorna los valores originales
  // Aquí se puede integrar la lógica compleja de CAC que ya tienes
  return {
    monto_actualizado: presupuesto.monto_presupuestado,
    saldo_actualizado:
      presupuesto.monto_presupuestado -
      gastos.reduce(
        (sum, g) =>
          sum +
          convertirMonto(g.monto, g.moneda, presupuesto.moneda, g.tipo_cambio),
        0
      ),
  };
};

// Función para sincronizar todos los proyectos
// Función para sincronizar todos los proyectos DE UNA CONSTRUCTORA
export const sincronizarTodosLosProyectosConstructora = async (
  constructoraId
) => {
  try {
    console.log(
      `🔄 Iniciando sincronización de todos los proyectos de constructora ${constructoraId}...`
    );

    const { data: proyectos, error } = await supabase
      .from("proyectos")
      .select("id, constructora_id")
      .eq("constructora_id", constructoraId);

    if (error) throw error;

    const resultados = {
      exitosos: 0,
      fallidos: 0,
      errores: [],
    };

    for (const proyecto of proyectos) {
      const resultado = await sincronizarPresupuestosProyecto(
        proyecto.id,
        proyecto.constructora_id
      );
      if (resultado.success) {
        resultados.exitosos++;
      } else {
        resultados.fallidos++;
        resultados.errores.push(`Proyecto ${proyecto.id}: ${resultado.error}`);
      }
    }

    console.log(
      `✅ Sincronización completa: ${resultados.exitosos} exitosos, ${resultados.fallidos} fallidos`
    );
    return resultados;
  } catch (error) {
    console.error("❌ Error en sincronización masiva:", error);
    return { success: false, error: error.message };
  }
};

// Función para sincronizar TODOS los proyectos (para el timer de 2 AM)
export const sincronizarTodosLosProyectos = async () => {
  try {
    console.log("🔄 Iniciando sincronización de TODOS los proyectos...");

    const { data: proyectos, error } = await supabase
      .from("proyectos")
      .select("id, constructora_id");

    if (error) throw error;

    const resultados = {
      exitosos: 0,
      fallidos: 0,
      errores: [],
    };

    for (const proyecto of proyectos) {
      const resultado = await sincronizarPresupuestosProyecto(
        proyecto.id,
        proyecto.constructora_id
      );
      if (resultado.success) {
        resultados.exitosos++;
      } else {
        resultados.fallidos++;
        resultados.errores.push(`Proyecto ${proyecto.id}: ${resultado.error}`);
      }
    }

    console.log(
      `✅ Sincronización completa: ${resultados.exitosos} exitosos, ${resultados.fallidos} fallidos`
    );
    return resultados;
  } catch (error) {
    console.error("❌ Error en sincronización masiva:", error);
    return { success: false, error: error.message };
  }
};
